import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { 
  Search, 
  X, 
  Filter,
  SlidersHorizontal,
  Crown,
  Sparkles,
  Star,
  ChevronDown,
  ChevronUp,
  Check
} from 'lucide-react';
import { Category } from '@/types/common';

interface MobileCategoryFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  categories?: Category[];
}

const MobileCategoryFilters = ({
  isOpen,
  onClose,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  categories
}: MobileCategoryFiltersProps) => {
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [expandedSections, setExpandedSections] = useState({
    search: true,
    categories: false,
    sort: false,
    price: false,
    features: false
  });

  const sortOptions = [
    { value: "name", label: "Name A-Z" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "featured", label: "Featured First" },
    { value: "newest", label: "Newest First" },
  ];

  const premiumFeatures = [
    { id: "premium", label: "Premium Quality", icon: Crown },
    { id: "limited", label: "Limited Edition", icon: Sparkles },
    { id: "exclusive", label: "Exclusive", icon: Star },
  ];

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleApplyFilters = () => {
    onClose();
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSortBy("name");
    setPriceRange([0, 2000]);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] bg-background border-t border-border/50 rounded-t-3xl"
      >
        <SheetHeader className="pb-6 border-b border-border/30">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl font-playfair flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              Sort & Refine
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="overflow-y-auto flex-1 py-6 space-y-6">
          {/* Search Section */}
          <div>
            <motion.button
              onClick={() => toggleSection('search')}
              className="flex items-center justify-between w-full mb-4"
              whileTap={{ scale: 0.98 }}
            >
              <h3 className="text-lg font-medium text-foreground">Search</h3>
              {expandedSections.search ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </motion.button>
            
            <AnimatePresence>
              {expandedSections.search && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-background/50 border-border/50"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Categories Section */}
          <div>
            <motion.button
              onClick={() => toggleSection('categories')}
              className="flex items-center justify-between w-full mb-4"
              whileTap={{ scale: 0.98 }}
            >
              <h3 className="text-lg font-medium text-foreground">Categories</h3>
              {expandedSections.categories ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </motion.button>
            
            <AnimatePresence>
              {expandedSections.categories && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2 overflow-hidden"
                >
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    className="w-full justify-between"
                    onClick={() => setSelectedCategory("all")}
                  >
                    <span>All Categories</span>
                    {selectedCategory === "all" && <Check className="h-4 w-4" />}
                  </Button>
                  
                  {categories?.map((category) => (
                    <Button
                      key={category.id}
                      variant={selectedCategory === category.id ? "default" : "outline"}
                      className="w-full justify-between"
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <span>{category.name}</span>
                      {selectedCategory === category.id && <Check className="h-4 w-4" />}
                    </Button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sort Section */}
          <div>
            <motion.button
              onClick={() => toggleSection('sort')}
              className="flex items-center justify-between w-full mb-4"
              whileTap={{ scale: 0.98 }}
            >
              <h3 className="text-lg font-medium text-foreground">Sort By</h3>
              {expandedSections.sort ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </motion.button>
            
            <AnimatePresence>
              {expandedSections.sort && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2 overflow-hidden"
                >
                  {sortOptions.map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? "default" : "outline"}
                      className="w-full justify-between"
                      onClick={() => setSortBy(option.value)}
                    >
                      <span>{option.label}</span>
                      {sortBy === option.value && <Check className="h-4 w-4" />}
                    </Button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Price Range Section */}
          <div>
            <motion.button
              onClick={() => toggleSection('price')}
              className="flex items-center justify-between w-full mb-4"
              whileTap={{ scale: 0.98 }}
            >
              <h3 className="text-lg font-medium text-foreground">Price Range</h3>
              {expandedSections.price ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </motion.button>
            
            <AnimatePresence>
              {expandedSections.price && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 overflow-hidden"
                >
                  <div className="px-2">
                    <Slider
                      value={priceRange}
                      onValueChange={setPriceRange}
                      max={2000}
                      min={0}
                      step={50}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm font-medium text-muted-foreground">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Premium Features Section */}
          <div>
            <motion.button
              onClick={() => toggleSection('features')}
              className="flex items-center justify-between w-full mb-4"
              whileTap={{ scale: 0.98 }}
            >
              <h3 className="text-lg font-medium text-foreground">Premium Features</h3>
              {expandedSections.features ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </motion.button>
            
            <AnimatePresence>
              {expandedSections.features && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  {premiumFeatures.map((feature) => (
                    <label
                      key={feature.id}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-2 border-border text-primary"
                      />
                      <feature.icon className="h-4 w-4 text-primary" />
                      <span className="text-foreground">
                        {feature.label}
                      </span>
                    </label>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="border-t border-border/30 pt-4 space-y-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={clearAllFilters}
          >
            <X className="h-4 w-4 mr-2" />
            Clear All Filters
          </Button>
          
          <Button
            className="w-full bg-primary text-primary-foreground"
            onClick={handleApplyFilters}
          >
            <Filter className="h-4 w-4 mr-2" />
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileCategoryFilters;